import { Injectable } from '@angular/core';
import { Event, NavigationExtras, Router, UrlTree } from '@angular/router';
import { BehaviorSubject, Observable, filter, firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OrgNavigationService {
    constructor(private router: Router) {}

    changeOrg(orgId: string) {
        let urls = this.router.url.split('/');
        urls = urls.filter(url => url !== '');

        urls[0] = orgId;
        this.router.navigateByUrl(urls.join('/'));
    }

    private shouldSkip(commands: any[] | string): boolean {
        const skipPrefixes = [
            'sign-in',
            'forgot-password',
            'reset-password',
            'sign-up',
            'sub-sign-in',
            'sign-out',
            'confirmation-required',
            'ms-teams',
        ];

        const segments = Array.isArray(commands)
            ? commands
            : commands.replace(/^\/+/, '').split('/');

        const first = typeof segments[0] === 'string' ? segments[0] : '';
        return skipPrefixes.includes(first);
    }

    private getCurrentOrgIdSafe(): string {
        const id = localStorage.getItem('organisation');
        if (!id) console.warn('[OrgNav] No orgId found');
        return id ?? '';
    }

    buildLink(commands: string[] | string | null | undefined): string[] {
        const orgId = this.getCurrentOrgIdSafe();

        if (!commands) {
            console.warn('[OrgNav] No commands provided to buildLink()');
            return [];
        }

        // Normalize into segments
        let normalized: string[];

        if (Array.isArray(commands)) {
            normalized = commands.map(c => (typeof c === 'string' ? c : ''));
            let norm: string[][] = normalized.map(c => c.replace(/^\/+/, '').split('/'));
            normalized = norm.flat();
            normalized = normalized.filter(c => c !== '');
        } else if (typeof commands === 'string') {
            if (commands.includes('%2F') || commands.startsWith('/')) {
                console.warn(
                    '[OrgNav] 🚨 You passed a raw string with slashes to router. Please use an array instead:',
                    commands
                );
            }
            normalized = commands.replace(/^\/+/, '').split('/');
        } else {
            normalized = [];
        }

        if (this.shouldSkip(normalized)) {
            return normalized;
        }

        const first = normalized[0];
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            first
        );
        const isAlreadyPrefixed = isUuid && first === orgId;

        return isAlreadyPrefixed ? normalized : [orgId, ...normalized];
    }

    // ✅ Smart org-prefixed navigate
    navigate(commands: any[] | string, extras?: NavigationExtras): Promise<boolean> {
        return this.router.navigate(this.buildLink(commands), extras);
    }

    navigateByUrl(url: string | UrlTree, extras?: NavigationExtras): Promise<boolean> {
        if (typeof url !== 'string') return this.router.navigateByUrl(url, extras);

        const segments = this.buildLink(url);
        const finalUrl = '/' + segments.join('/');

        return this.router.navigateByUrl(finalUrl, extras);
    }

    // ✅ Delegate all other Router methods & properties
    createUrlTree(
        ...args: Parameters<Router['createUrlTree']>
    ): ReturnType<Router['createUrlTree']> {
        return this.router.createUrlTree(...args);
    }

    serializeUrl(url: UrlTree): string {
        return this.router.serializeUrl(url);
    }

    parseUrl(url: string): UrlTree {
        return this.router.parseUrl(url);
    }

    isActive(url: string | UrlTree, otherOptions: any): boolean {
        return this.router.isActive(url, otherOptions);
    }

    get url(): string {
        return this.router.url;
    }

    get events(): Observable<Event> {
        return this.router.events;
    }

    get routerState() {
        return this.router.routerState;
    }

    get navigated(): boolean {
        return this.router.navigated;
    }

    get urlUpdates(): Observable<void> {
        return (this.router as any).urlUpdates;
    }

    // Optional: Add more passthroughs as needed
}
